import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Send, Loader2, AlertCircle, ExternalLink, Settings2 } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';
import { useLanguage } from '../contexts/LanguageContext';

const TELEGRAM_BOT_URL = process.env.REACT_APP_TELEGRAM_BOT_URL || 'https://t.me/Murphi707_bot';
const API = process.env.REACT_APP_BACKEND_URL;

const t = {
  ar: {
    polling: 'نتحقق من حالة الدفع...',
    paidH: 'تم الاشتراك بنجاح',
    paidSub: 'مرحباً بك في Quidanalyzer المميّز. ستبدأ الإشارات والتنبيهات بالوصول إليك على تيليجرام مباشرة.',
    pendingH: 'الدفع قيد المعالجة',
    pendingSub: 'قد يستغرق ذلك لحظات. سنتابع الحالة تلقائياً.',
    expiredH: 'انتهت صلاحية جلسة الدفع',
    expiredSub: 'حاول مجدداً من صفحة Quidanalyzer.',
    timeoutH: 'انتهت مهلة التحقق',
    timeoutSub: 'يرجى التحقق من بريدك الإلكتروني للحصول على تأكيد الدفع.',
    errorH: 'تعذر التحقق من الحالة',
    next: 'الخطوات التالية',
    step1: 'افتح بوت Quidanalyzer على تيليجرام',
    step2: 'أرسل الأمر /start لربط اشتراكك',
    openBot: 'افتح البوت الآن',
    manage: 'إدارة الاشتراك',
    back: 'العودة إلى Quidanalyzer',
    missingSession: 'لم نتلقَّ معرّف جلسة الدفع. يرجى العودة إلى صفحة Quidanalyzer.',
    autoRedirect: 'سيتم فتح تيليجرام تلقائياً خلال',
    seconds: 'ث',
    cancelAuto: 'البقاء على هذه الصفحة',
    opened: 'فتحنا تيليجرام في تبويب جديد. إن لم يفتح، استخدم الزر بالأسفل.',
  },
  en: {
    polling: 'Checking your payment status...',
    paidH: 'Subscription active',
    paidSub: 'Welcome to Quidanalyzer Premium. Signals and alerts will start flowing into your Telegram now.',
    pendingH: 'Payment is processing',
    pendingSub: 'This usually takes a few seconds. We\u2019ll keep checking automatically.',
    expiredH: 'Checkout session expired',
    expiredSub: 'Please try again from the Quidanalyzer page.',
    timeoutH: 'Status check timed out',
    timeoutSub: 'Please check your email for payment confirmation.',
    errorH: 'Could not verify status',
    next: 'Next steps',
    step1: 'Open the Quidanalyzer bot on Telegram',
    step2: 'Send /start to link your subscription',
    openBot: 'Open the bot now',
    manage: 'Manage subscription',
    back: 'Back to Quidanalyzer',
    missingSession: 'We didn\u2019t receive a checkout session ID. Please return to the Quidanalyzer page.',
    autoRedirect: 'Opening Telegram automatically in',
    seconds: 's',
    cancelAuto: 'Stay on this page',
    opened: 'We opened Telegram in a new tab. If it didn\u2019t open, use the button below.',
  },
};

const QuidanalyzerSuccessPage = () => {
  const { language, isRTL } = useLanguage();
  const c = t[language];
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [state, setState] = useState({ phase: 'polling', detail: null });
  const attempts = useRef(0);
  const MAX_ATTEMPTS = 6;
  const INTERVAL = 2200;

  // ─── Auto-redirect to Telegram bot ──────────────────────────────────────────
  // When the subscription flips to 'paid', start a 3s countdown and then
  // open the Telegram bot in a new tab. User can cancel by clicking
  // "Stay on this page" or by clicking the manual "Open the bot now" CTA.
  const REDIRECT_DELAY = 3;
  const [countdown, setCountdown] = useState(null);       // null | 3 | 2 | 1 | 0
  const [autoOpened, setAutoOpened] = useState(false);    // true once window.open ran
  const redirectTimer = useRef(null);
  const hasRedirected = useRef(false);                    // guard against StrictMode double-fire

  useEffect(() => {
    if (state.phase !== 'paid') return;
    // Guard: only schedule once per page lifetime + only once per session_id (across reloads)
    const storageKey = sessionId ? `quidory.qa-redirected:${sessionId}` : null;
    let alreadyDone = false;
    if (storageKey) {
      try { alreadyDone = window.sessionStorage.getItem(storageKey) === '1'; } catch { /* ignore */ }
    }
    if (hasRedirected.current || alreadyDone) {
      setAutoOpened(true);
      return;
    }

    setCountdown(REDIRECT_DELAY);
    let n = REDIRECT_DELAY;
    redirectTimer.current = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(redirectTimer.current);
        redirectTimer.current = null;
        if (hasRedirected.current) return;
        hasRedirected.current = true;
        try {
          window.open(TELEGRAM_BOT_URL, '_blank', 'noopener,noreferrer');
        } catch { /* ignore */ }
        if (storageKey) {
          try { window.sessionStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
        }
        setAutoOpened(true);
        setCountdown(null);
      } else {
        setCountdown(n);
      }
    }, 1000);

    return () => {
      if (redirectTimer.current) {
        clearInterval(redirectTimer.current);
        redirectTimer.current = null;
      }
    };
  }, [state.phase, sessionId]);

  const cancelAutoRedirect = () => {
    if (redirectTimer.current) {
      clearInterval(redirectTimer.current);
      redirectTimer.current = null;
    }
    hasRedirected.current = true;          // prevent any further auto-open
    setCountdown(null);
  };

  useEffect(() => {
    let cancelled = false;
    let timerId = null;
    if (!sessionId) {
      setState({ phase: 'missing' });
      return;
    }
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/api/quidanalyzer/checkout-status/${sessionId}`);
        const { status, payment_status } = res.data || {};
        if (cancelled) return;
        if (payment_status === 'paid') {
          setState({ phase: 'paid', detail: res.data });
          return;
        }
        if (status === 'expired') {
          setState({ phase: 'expired' });
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ phase: 'timeout' });
          return;
        }
        timerId = setTimeout(poll, INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ phase: 'error', detail: e?.response?.data?.detail || String(e) });
          return;
        }
        timerId = setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [sessionId]);

  const openPortal = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.post(`${API}/api/quidanalyzer/portal`, {
        session_id: sessionId,
        origin: window.location.origin,
        lang: language,
      });
      if (res?.data?.url) window.location.href = res.data.url;
    } catch (e) {
      // surface inline with state
      setState(s => ({ ...s, portalError: e?.response?.data?.detail || (language === 'ar' ? 'تعذر فتح بوابة الاشتراك' : 'Could not open subscription portal') }));
    }
  };

  return (
    <>
      <SEO
        titleEn="Subscription Confirmed | Quidanalyzer"
        titleAr="تم تأكيد الاشتراك | Quidanalyzer"
        descEn="Your Quidanalyzer Premium subscription is being confirmed. Open the bot on Telegram to start receiving signals."
        descAr="جارٍ تأكيد اشتراكك في Quidanalyzer المميّز. افتح البوت على تيليجرام لتبدأ باستلام الإشارات."
        path="/quidanalyzer/success"
      />
      <div className="pt-16 min-h-[calc(100vh-4rem)] flex items-center bg-background">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          {/* Status card */}
          <div className="rounded-2xl border border-border bg-card p-8 sm:p-10 text-center">
            {state.phase === 'polling' && (
              <>
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-emerald-500/10 mb-5">
                  <Loader2 className="h-6 w-6 text-emerald-400 animate-spin" />
                </div>
                <h1 className="text-2xl font-light text-foreground" data-testid="qa-success-h">{c.polling}</h1>
              </>
            )}

            {state.phase === 'paid' && (
              <>
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-5">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-light text-foreground" data-testid="qa-success-h">{c.paidH}</h1>
                <p className="mt-3 text-sm text-muted-foreground">{c.paidSub}</p>

                <div className="mt-8 text-start rtl:text-start space-y-3">
                  <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">{c.next}</p>
                  <ol className="space-y-2 text-sm text-foreground/85">
                    <li className="flex items-start gap-2">
                      <span className="font-mono text-emerald-400 text-xs">01</span>
                      <span>{c.step1}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-mono text-emerald-400 text-xs">02</span>
                      <span>{c.step2}</span>
                    </li>
                  </ol>
                </div>

                {/* Auto-redirect banner */}
                {countdown !== null && countdown > 0 && (
                  <div
                    data-testid="qa-success-autoredirect"
                    className="mt-6 px-4 py-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between"
                  >
                    <p className="text-xs text-emerald-200/90 inline-flex items-center gap-2 justify-center sm:justify-start">
                      <Send className="h-3.5 w-3.5 text-emerald-400" />
                      <span>{c.autoRedirect}</span>
                      <span
                        data-testid="qa-success-countdown"
                        className="font-mono font-bold text-emerald-300 text-sm tabular-nums"
                        dir="ltr"
                      >
                        {countdown}{c.seconds}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={cancelAutoRedirect}
                      data-testid="qa-success-cancel-auto"
                      className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                    >
                      {c.cancelAuto}
                    </button>
                  </div>
                )}
                {autoOpened && (
                  <p
                    data-testid="qa-success-opened-note"
                    className="mt-6 text-xs text-muted-foreground/80 inline-flex items-center gap-2 justify-center"
                  >
                    <ExternalLink className="h-3 w-3 text-emerald-400" />
                    {c.opened}
                  </p>
                )}

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href={TELEGRAM_BOT_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={cancelAutoRedirect}
                    data-testid="qa-success-open-bot"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 text-emerald-950 text-sm font-semibold hover:bg-emerald-400 transition-all hover:-translate-y-px"
                  >
                    <Send className="h-4 w-4" />
                    {c.openBot}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={openPortal}
                    data-testid="qa-success-manage"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-border bg-background text-foreground text-sm font-medium hover:bg-muted/50 transition-colors"
                  >
                    <Settings2 className="h-4 w-4 text-muted-foreground" />
                    {c.manage}
                  </button>
                </div>
                {state.portalError && (
                  <p className="mt-3 text-xs text-red-400">{state.portalError}</p>
                )}
              </>
            )}

            {state.phase === 'expired' && (
              <>
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-red-500/15 border border-red-500/30 mb-5">
                  <AlertCircle className="h-6 w-6 text-red-400" />
                </div>
                <h1 className="text-2xl font-light text-foreground" data-testid="qa-success-h">{c.expiredH}</h1>
                <p className="mt-3 text-sm text-muted-foreground">{c.expiredSub}</p>
              </>
            )}

            {state.phase === 'timeout' && (
              <>
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-yellow-500/15 border border-yellow-500/30 mb-5">
                  <AlertCircle className="h-6 w-6 text-yellow-400" />
                </div>
                <h1 className="text-2xl font-light text-foreground" data-testid="qa-success-h">{c.timeoutH}</h1>
                <p className="mt-3 text-sm text-muted-foreground">{c.timeoutSub}</p>
              </>
            )}

            {state.phase === 'error' && (
              <>
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-red-500/15 border border-red-500/30 mb-5">
                  <AlertCircle className="h-6 w-6 text-red-400" />
                </div>
                <h1 className="text-2xl font-light text-foreground" data-testid="qa-success-h">{c.errorH}</h1>
                <p className="mt-3 text-sm text-muted-foreground">{state.detail}</p>
              </>
            )}

            {state.phase === 'missing' && (
              <>
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-yellow-500/15 border border-yellow-500/30 mb-5">
                  <AlertCircle className="h-6 w-6 text-yellow-400" />
                </div>
                <h1 className="text-2xl font-light text-foreground" data-testid="qa-success-h">{c.errorH}</h1>
                <p className="mt-3 text-sm text-muted-foreground">{c.missingSession}</p>
              </>
            )}

            {state.phase !== 'paid' && (
              <div className="mt-8">
                <LocaleLink
                  to="/quidanalyzer"
                  className="text-sm text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline"
                  data-testid="qa-success-back"
                >
                  {isRTL ? '←' : '\u2190'} {c.back}
                </LocaleLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default QuidanalyzerSuccessPage;
